/*
 * The account API answers errors with RFC 9457 `application/problem+json`, always with a `code`
 * (apps/account-api/src/problem.ts). Failures that never reached it get a code of their own:
 * `network_error` (no response) and `signed_out` (no session to send).
 */

export interface FieldProblem {
  /** JSON pointer of the field, e.g. `/givenName`. */
  path: string;
  message: string;
}

export interface Problem {
  /** HTTP status; 0 when no response arrived. */
  status: number;
  code: string;
  title?: string;
  detail?: string;
  errors?: FieldProblem[];
  retryAfterSeconds?: number;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title ?? problem.code);
    this.problem = problem;
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string {
    return this.problem.code;
  }
}

function isFieldProblem(value: unknown): value is FieldProblem {
  if (typeof value !== 'object' || value === null) return false;
  const { path, message } = value as Record<string, unknown>;
  return typeof path === 'string' && typeof message === 'string';
}

/** The problem a non-2xx response carries, or one made from its status. */
export function problemFrom(status: number, body: unknown, headers?: Headers): Problem {
  const problem: Problem = { status, code: `http_${status}` };
  if (typeof body === 'object' && body !== null) {
    const { code, title, detail, errors, retryAfterSeconds } = body as Record<string, unknown>;
    if (typeof code === 'string' && code !== '') problem.code = code;
    if (typeof title === 'string') problem.title = title;
    if (typeof detail === 'string') problem.detail = detail;
    if (Array.isArray(errors)) problem.errors = errors.filter(isFieldProblem);
    if (typeof retryAfterSeconds === 'number') problem.retryAfterSeconds = retryAfterSeconds;
  }
  const retryAfter = Number(headers?.get('retry-after') ?? Number.NaN);
  if (problem.retryAfterSeconds === undefined && Number.isFinite(retryAfter) && retryAfter >= 0) {
    problem.retryAfterSeconds = retryAfter;
  }
  return problem;
}

export async function apiErrorFrom(response: Response): Promise<ApiError> {
  let body: unknown;
  if (/json/i.test(response.headers.get('content-type') ?? '')) {
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
  }
  return new ApiError(problemFrom(response.status, body, response.headers));
}

export const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
export const NETWORK_MESSAGE =
  "We couldn't reach the Direwolf AI account service. Check your connection and try again.";
export const SESSION_ENDED_MESSAGE = 'Your session has ended. Sign in again to continue.';
export const UNAVAILABLE_MESSAGE =
  'The Direwolf AI account service is temporarily unavailable. Please try again shortly.';

/** Problems whose `detail` the API words for the person in front of the screen. */
const USER_WORDED = new Set([
  'account_not_found',
  'validation_failed',
  'confirmation_required',
  'reauthentication_required',
  'rate_limited',
  'directory_unavailable',
  'storage_unavailable',
  'deletion_unavailable',
]);

const SESSION_CODES = new Set(['signed_out', 'unauthenticated', 'token_expired', 'token_invalid']);

/** Whether only a new sign-in can fix this (the session is missing, expired or refused). */
export function isSessionEnded(error: unknown): boolean {
  return error instanceof ApiError && SESSION_CODES.has(error.code);
}

/** What to tell the person about a failed request. */
export function errorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return GENERIC_MESSAGE;
  if (error.code === 'network_error') return NETWORK_MESSAGE;
  if (isSessionEnded(error)) return SESSION_ENDED_MESSAGE;
  const { detail } = error.problem;
  if (USER_WORDED.has(error.code) && detail !== undefined && detail !== '') return detail;
  if (error.status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (error.code === 'not_configured' || error.status >= 500) return UNAVAILABLE_MESSAGE;
  return GENERIC_MESSAGE;
}
